'use strict';
function cartLineIdentity(line) {
  return JSON.stringify([String(line.variant_id),Object.entries(line.properties||{}).sort(([a],[b])=>a.localeCompare(b)),String(line.selling_plan_allocation?.selling_plan?.id||''),line.original_price,line.final_price,line.parent_relationship||null]);
}
function logicalCart(cart) {
  if(!isCart(cart))throw new Error('Invalid cart snapshot');
  const groups=new Map();
  for(const line of cart.items){
    const identity=cartLineIdentity(line);
    const sources=line._doma_sources||[{key:line.key,quantity:line.quantity}];
    if(!groups.has(identity)){groups.set(identity,{...line,_doma_sources:sources.map(source=>({...source}))});continue;}
    const group=groups.get(identity);
    group.quantity+=line.quantity;
    group._doma_sources.push(...sources.map(source=>({...source})));
    for(const field of ['original_line_price','final_line_price','line_price','total_discount']){
      if(Number.isSafeInteger(group[field])&&Number.isSafeInteger(line[field]))group[field]+=line[field];
    }
  }
  return {...cart,items:Array.from(groups.values())};
}
function checkoutCart(cart) {
  const grouped=logicalCart(cart);
  return {...grouped,items:grouped.items.map(line=>{const {_doma_sources,...item}=line;return {...item,properties:pruneUnitProperties(item.properties,item.quantity)};})};
}
function pruneUnitProperties(properties, quantity) {
  const result={...properties};
  for(const key of Object.keys(result)){const unit=key.match(/^Gravação unidade (\d+)$/);if(unit&&Number(unit[1])>quantity)delete result[key];}
  return result;
}
async function changeLogicalQuantity(send, original, expected, quantity) {
  if(!Number.isSafeInteger(quantity)||quantity<0||quantity>100)throw new Error('Invalid quantity');
  let current=logicalCart(original),target=resolveUnchangedCartLine(current,expected);
  if(!target)throw new Error('Cart changed');
  const cleaned=pruneUnitProperties(target.properties,target.quantity);
  if(JSON.stringify(cleaned)!==JSON.stringify(target.properties||{})){
    current=await confirmEngravingChanges(send,current,[{expected:target,properties:cleaned}]);
    expected={...expected,properties:cleaned};
    target=resolveUnchangedCartLine(current,expected);
    if(!target)throw new Error('Cart changed');
  }
  for(let step=0;step<100;step++){
    if(target.quantity===quantity){
      const properties=pruneUnitProperties(target.properties,quantity);
      if(JSON.stringify(properties)!==JSON.stringify(target.properties||{}))return confirmEngravingChanges(send,current,[{expected:target,properties}]);
      return current;
    }
    const source=target._doma_sources[0],delta=quantity-target.quantity;
    const adjustment=delta>0?delta:Math.max(delta,-source.quantity);
    const previous=target.quantity;
    const response=await send('cart/change.js',{id:source.key,quantity:source.quantity+adjustment});
    if(!isCart(response))throw new Error('Invalid cart response');
    current=logicalCart(response);
    const matches=current.items.filter(line=>cartLineIdentity(line)===cartLineIdentity(expected));
    if(!matches.length&&previous+adjustment===0)return current;
    if(matches.length!==1||matches[0].quantity!==previous+adjustment)throw new Error('Quantity confirmation failed');
    target=matches[0];
  }
  throw new Error('Quantity confirmation failed');
}
async function confirmEngravingChanges(send, original, changes) {
  original=logicalCart(original);
  let updated;
  for(const change of changes){
    const target=resolveUnchangedCartLine(updated||original,change.expected);
    if(!target)throw new Error('Os itens mudaram durante a gravação. Atualize o carrinho para conferir.');
    const source=target._doma_sources[0];
    updated=await send('cart/change.js',{id:source.key,quantity:source.quantity,properties:change.properties});
    if(!isCart(updated))updated=await send('cart.js');
    if(!isCart(updated))throw new Error('A resposta do carrinho está incompleta. Atualize para conferir.');
    updated=logicalCart(updated);
    if(!resolveUnchangedCartLine(updated,{...target,properties:change.properties}))throw new Error('A gravação não foi confirmada na peça selecionada. Atualize para conferir.');
  }
  return updated;
}
function resolveUnchangedCartLine(cart, expected) {
  const signature=properties=>JSON.stringify(Object.entries(properties||{}).sort(([a],[b])=>a.localeCompare(b)));
  const unchanged=line=>String(line.variant_id)===String(expected.variant_id)&&line.quantity===expected.quantity&&signature(line.properties)===signature(expected.properties)&&String(line.selling_plan_allocation?.selling_plan?.id||'')===String(expected.selling_plan_allocation?.selling_plan?.id||'');
  const keyed=cart.items.find(line=>line.key===expected.key);
  if(keyed)return unchanged(keyed)?keyed:null;
  const candidates=cart.items.filter(unchanged);
  return candidates.length===1?candidates[0]:null;
}
function engravingDraftIdentity(line) {
  return JSON.stringify([line.key,line.variant_id,line.properties||{},line.selling_plan_allocation?.selling_plan?.id||null]);
}
function engravingDismissIdentity(cart, entry) {
  const target=cart.items.find(line=>line.key===entry.key);
  if(!target||!Number.isInteger(entry.unit)||entry.unit<1||entry.unit>target.quantity)return null;
  const group=line=>String(line.variant_id)+':'+String(line.selling_plan_allocation?.selling_plan?.id||'');
  const id=group(target);let offset=0;
  for(const line of cart.items){if(line.key===entry.key)break;if(group(line)===id)offset+=line.quantity;}
  return JSON.stringify([id,offset+entry.unit]);
}
function pruneEngravingDismissals(cart, dismissed) {
  const before=dismissed.size;
  const totals=new Map();
  for(const line of cart.items){const id=String(line.variant_id)+':'+String(line.selling_plan_allocation?.selling_plan?.id||'');totals.set(id,(totals.get(id)||0)+line.quantity);}
  for(const saved of dismissed){try{
    const [id,unit]=JSON.parse(saved);
    if(!totals.has(id)||unit>totals.get(id)){dismissed.delete(saved);continue;}
    let offset=0;
    for(const line of cart.items){
      if(String(line.variant_id)+':'+String(line.selling_plan_allocation?.selling_plan?.id||'')!==id)continue;
      if(unit<=offset+line.quantity){const position=unit-offset,key=position===1?'Gravar Nome (Opcional)':'Gravação unidade '+position;if(String(line.properties?.[key]||'').trim())dismissed.delete(saved);break;}
      offset+=line.quantity;
    }
  }catch{dismissed.delete(saved);}}
  return before!==dismissed.size;
}
function normalizedRules(rules) {
  const id=value=>String(value??'').split('/').pop();
  if(!rules||rules.enabled!==true)return null;
  if(rules.campaign==='fileteira_pair'){
    const sharpener=id(rules.sharpener),fillets=Array.isArray(rules.fillets)?rules.fillets.map(id):[];
    if(!/^\d+$/.test(sharpener)||!fillets.length||fillets.some(v=>!/^\d+$/.test(v))||new Set(fillets).size!==fillets.length)return null;
    return {campaign:'fileteira_pair',sharpener,fillets,regular:[],snipers:[],knives:[],giftVariants:[sharpener]};
  }
  const gifts=[rules.sharpener,rules.opener,rules.box].map(id);
  if(gifts.some(v=>!/^\d+$/.test(v))||new Set(gifts).size!==3)return null;
  if(!['regular','snipers','fillets'].every(k=>Array.isArray(rules[k])))return null;
  const groups=Object.fromEntries(['regular','snipers','fillets'].map(k=>[k,rules[k].map(id)]));
  const products=Object.values(groups).flat();
  if(products.some(v=>!/^\d+$/.test(v))||new Set(products).size!==products.length)return null;
  if(rules.campaign && rules.campaign!=='sharpener_per_knife')return null;
  if(rules.campaign==='sharpener_per_knife'&&(!Number.isSafeInteger(rules.minimumUnitPriceCents)||rules.minimumUnitPriceCents<1))return null;
  const knives=Array.isArray(rules.knives)?rules.knives.map(id):[];
  if(rules.campaign==='sharpener_per_knife'&&(!knives.length||knives.some(v=>!/^\d+$/.test(v))))return null;
  return {...groups,knives,campaign:rules.campaign,minimumUnitPriceCents:rules.minimumUnitPriceCents,sharpener:gifts[0],opener:gifts[1],box:gifts[2],giftVariants:gifts};
}
function choicesFor(line) {
  try { const value=JSON.parse(line.properties?._doma_choices||'[]');return Array.isArray(value)?value.slice(0,line.quantity).map(v=>v==='sharpener'?'sharpener':'opener'):[]; } catch {return [];}
}
function giftPlan(cart, config) {
  const rules=normalizedRules(config);
  if(!rules)return {};
  const result = new Map();
  const add = (variant, quantity) => { if (variant && quantity > 0) result.set(String(variant), (result.get(String(variant)) || 0) + quantity); };
  let fillets = 0;
  for (const line of cart.items) {
    if(!Number.isSafeInteger(line.quantity)||line.quantity<=0)continue;
    if (rules.giftVariants.map(String).includes(String(line.variant_id))) continue;
    const id = String(line.product_id);
    if(rules.campaign==='sharpener_per_knife'){
      if(cart.currency==='BRL'&&Number.isSafeInteger(line.original_price)&&line.original_price>=rules.minimumUnitPriceCents&&rules.knives.includes(id))add(rules.sharpener,line.quantity);
      continue;
    }
    if (rules.fillets.map(String).includes(id)) { fillets += line.quantity; continue; }
    if (rules.snipers.map(String).includes(id)) {
      const choices=choicesFor(line);
      const sharpeners=choices.filter(choice=>choice==='sharpener').length;
      add(rules.sharpener,sharpeners);
      add(rules.opener,line.quantity-sharpeners);
      add(rules.box, line.quantity);
    } else if (rules.regular.map(String).includes(id)) {
      add(rules.sharpener, line.quantity); add(rules.box, line.quantity);
    }
  }
  if (fillets >= 2) add(rules.sharpener, 1);
  return Object.fromEntries(result);
}
function isManagedGift(line,config) {
  return line.properties?._doma_brinde==='automatico';
}
function giftChanges(cart,config) {
  const remaining={...giftPlan(cart,config)},changes=[];
  for(const line of cart.items){
    if(!isManagedGift(line,config))continue;
    const id=String(line.variant_id),wanted=Math.min(line.quantity,remaining[id]||0);
    remaining[id]=Math.max(0,(remaining[id]||0)-wanted);
    if(line.quantity!==wanted)changes.push({path:'cart/change.js',body:{id:line.key,quantity:wanted}});
  }
  const items=Object.entries(remaining).filter(([,quantity])=>quantity>0).map(([id,quantity])=>({id:Number(id),quantity,properties:{_doma_brinde:'automatico'}}));
  if(items.length)changes.push({path:'cart/add.js',body:{items}});
  return changes;
}
function giftsVerified(cart,config) {
  if(giftChanges(cart,config).length)return false;
  return cart.items.filter(i=>isManagedGift(i,config)).every(i=>i.final_line_price===0);
}
function isCart(value) {
  return !!value&&Array.isArray(value.items)&&Number.isSafeInteger(value.item_count)&&Number.isSafeInteger(value.total_price)&&typeof value.currency==='string';
}
async function reconcileGifts(request,config,initialCart) {
  let cart=isCart(initialCart)?initialCart:await request('cart.js');
  for(let step=0;step<12;step++){
    const changes=giftChanges(cart,config);
    if(!changes.length){
      if(cart.items.some(i=>isManagedGift(i,config)&&i.final_line_price!==0)){
        config.enabled=false;
        continue;
      }
      return cart;
    }
    const change=changes[0];
    const updated=await request(change.path,change.body);
    cart=change.path==='cart/change.js'&&isCart(updated)?updated:await request('cart.js');
  }
  throw new Error('O carrinho mudou durante a atualização dos brindes. Confira os itens e tente novamente.');
}